const MAX_NAME = 120;

// A trailing dot-segment only counts as an extension if it looks like one.
// Splitting naively on the last dot mangles names that merely contain dots:
// "../../etc/passwd" would yield extension "./etc/passwd", and
// "Lecture v1.2 notes" would yield ".2 notes".
const EXTENSION = /^\.[A-Za-z0-9]{1,8}$/;

/**
 * S3 keys tolerate most characters, but path separators and query-ish
 * characters make objects awkward to address later. Everything outside a
 * conservative set becomes an underscore.
 */
export function sanitizeFilename(name) {
  const raw = String(name ?? '');
  const dot = raw.lastIndexOf('.');
  const candidate = dot > 0 ? raw.slice(dot) : '';
  const hasExt = EXTENSION.test(candidate);

  let stem = hasExt ? raw.slice(0, dot) : raw;
  let ext = hasExt ? candidate : '';

  const clean = (s) => s.replace(/[^A-Za-z0-9._-]+/g, '_').replace(/_{2,}/g, '_');

  stem = clean(stem);
  ext = clean(ext);

  if (!stem.replace(/[._-]/g, '')) {
    if (!ext.replace(/[._-]/g, '')) return 'recording';
    stem = 'recording';
  }

  const room = Math.max(1, MAX_NAME - ext.length);
  return stem.slice(0, room) + ext;
}

/**
 * memos/<identity>/YYYY/MM/DD/<epoch-ms>-<name>
 *
 * Date-partitioned so Alter can later fetch a week with one prefix query
 * instead of listing the bucket. The epoch prefix keeps two same-named
 * recordings on one day apart.
 */
export function buildObjectKey(identityId, filename, now) {
  const yyyy = now.getUTCFullYear();
  const mm = String(now.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(now.getUTCDate()).padStart(2, '0');
  return `memos/${identityId}/${yyyy}/${mm}/${dd}/${now.getTime()}-${sanitizeFilename(filename)}`;
}

/**
 * The class a recording belongs to, in the only form S3 will carry.
 *
 * This is the one moment the class is knowable. Whisper cannot hear it, the
 * filename does not hold it, and asking afterwards means asking about a
 * recording you have stopped thinking about.
 *
 * S3 metadata values must be US-ASCII: a header is not UTF-8, and Canvas
 * course names contain em dashes and accents often enough to matter.
 */
export function courseMetadata(cls) {
  if (!cls) throw new Error('a recording must know which class it is for');

  // A course titled entirely in a non-Latin script strips to '' here, and an
  // empty S3 metadata value is legal — so the upload would succeed while
  // filing the recording nowhere. Each field falls back on its own value,
  // not on the other field's, so an ASCII code survives a non-ASCII name.
  const fallback = `Course ${cls.id}`;

  return {
    'course-code': ascii(cls.meta || cls.name) || fallback,
    'course-name': ascii(cls.name) || fallback,
    'course-id': String(cls.id ?? ''),
  };
}

function ascii(text) {
  return String(text ?? '')
    .normalize('NFKD')
    .replace(/[‐-―]/g, '-') // the dash family, which Canvas loves
    // A stripped character becomes a space rather than vanishing, so two
    // words that only had a non-ASCII character between them ("Music\n101")
    // do not get glued into one ("Music101"). The run collapse below then
    // cleans up the case where several stripped characters sat in a row.
    .replace(/[^\x20-\x7e]/g, ' ')
    .replace(/ {2,}/g, ' ')
    .trim();
}
